/**
 * Shared utility functions for Product Details Modals
 * This file contains reusable logic to reduce code duplication between
 * BulkProductDetailsModal and ProductDetailsModal while maintaining exact same logic
 */

import {Constants} from '@/utils/constants/commonConstants';

// Initial form data structure
export const getInitialFormData = () => ({
  points: '',
  quota: {
    unlimited: false,
    value: ''
  },
  inventory: ''
});

// Initial errors structure
export const getInitialErrors = () => ({
  points: '',
  quota: '',
  inventory: ''
});

// Initialize form data from product details
export const initializeFormDataFromProduct = (product) => {
  if (!product?.details) return getInitialFormData();

  const initialInventory = product.details.inventory || '';
  return {
    points: product.details.points || '',
    quota: {
      unlimited: product.details.quota?.unlimited || false,
      value: product.details.quota?.value || ''
    },
    inventory: initialInventory
  };
};

// Create points change handler
export const createPointsHandler =
  (validatePoints, setErrors, setFormData) => (value) => {
    const validationError = validatePoints(value);
    setErrors((prev) => ({
      ...prev,
      points: validationError || ''
    }));
    setFormData((prev) => ({
      ...prev,
      points: value
    }));
  };

// Create inventory change handler
export const createInventoryHandler =
  (validateInventory, setErrors, setFormData) => (value) => {
    const validationError = validateInventory(value);
    setErrors((prev) => ({
      ...prev,
      inventory: validationError || ''
    }));
    setFormData((prev) => ({
      ...prev,
      inventory: value
    }));
  };

// Create quota change handler
export const createQuotaHandler =
  (validateQuotaValue, setErrors, setFormData, formData) => (field, value) => {
    if (field === 'unlimited') {
      // When toggling unlimited, clear quota error
      setErrors((prev) => ({
        ...prev,
        quota: ''
      }));
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          quota: {
            ...prev.quota,
            unlimited: value
          }
        };

        // If unlimited is true, set inventory to "Unlimited"
        if (value) {
          newFormData.inventory = Constants.Unlimited;
        } else {
          // If unlimited is false, set inventory to quota value or empty
          newFormData.inventory = prev.quota.value || '';
        }

        return newFormData;
      });
    } else if (field === 'value') {
      // Validate quota value only if unlimited is false
      const validationError = formData.quota.unlimited
        ? ''
        : validateQuotaValue(value);
      setErrors((prev) => ({
        ...prev,
        quota: validationError || ''
      }));
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          quota: {
            ...prev.quota,
            value: value
          }
        };

        // Always set inventory to quota value (unless unlimited is true)
        if (!prev.quota.unlimited) {
          newFormData.inventory = value;
        }

        return newFormData;
      });
    }
  };

// Create form validator
export const createFormValidator =
  (validatePoints, validateQuotaValue, formData) => () => {
    // Points must be valid
    const pointsError = validatePoints(formData.points);
    if (pointsError) return false;

    // Quota value must be valid only if unlimited is false
    if (!formData.quota.unlimited) {
      const quotaError = validateQuotaValue(formData.quota.value);
      if (quotaError) return false;
    }

    // Inventory is not required as it's always auto-filled
    return true;
  };

// Create form validator with error setting
export const createFormValidatorWithErrors =
  (validatePoints, validateQuotaValue, formData, setErrors) => () => {
    const newErrors = {};

    // Validate points
    const pointsError = validatePoints(formData.points);
    if (pointsError) {
      newErrors.points = pointsError;
    }

    // Validate quota value only if unlimited is false
    if (!formData.quota.unlimited) {
      const quotaError = validateQuotaValue(formData.quota.value);
      if (quotaError) {
        newErrors.quota = quotaError;
      }
    }

    // Inventory is not required as it's always auto-filled
    // No inventory validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

// Check if inventory field should be disabled (always disabled now)
export const isInventoryDisabled = () => true;
